import { unref } from 'vue';
import { createStore as _createStore } from 'vuex';
import { mapInstanceState, mapInstanceGetters, mapInstanceMutations, mapInstanceActions } from '@urbn/vuex-helpers'; // eslint-disable-line
import VuexPersistence from 'vuex-persist';
import globals from '@/globals';
import LANG from '@/lang/eng';
import leftMenu from '@/store/modules/left_menu/';
import moduleUI from '@/store/modules/ui/';
import moduleTags from '@/store/modules/tags/';
import entityBrowser from '@/store/modules/entity_browser';
import { BrowserMode } from '@/store/modules/entity_browser/config';
import acl from '@/store/acl';
import roles from '@/store/roles';
import user from '@/store/user';
import locale from '@/store/locale';
import browserMode from '@/store/browserMode';
import clipboard, { copyBroadcaster } from '@/store/clipboard';

const systemConfig = globals.I && globals.I.settings && globals.I.settings.system;

const modules = {
  [BrowserMode.MAM]: entityBrowser(BrowserMode.MAM, systemConfig),
  [BrowserMode.DOC]: entityBrowser(BrowserMode.DOC, systemConfig),
  ui: moduleUI,
  tags: moduleTags,
  left_menu: leftMenu,
  roles,
  clipboard,
};

const hasPersistence = (m) => m.persistence && typeof m.persistence.reducer === 'function';
const modulePersistenceReducers = Object.fromEntries(
  Object.entries(modules)
    .filter(([, module]) => hasPersistence(module))
    .map(([key, { persistence }]) => [key, persistence.reducer]) // eslint-disable-line
);

const persistenceReducer = (state) => (
  Object.entries(modulePersistenceReducers)
    .reduce((acc, [key, reducer]) => ({
      ...acc,
      [key]: reducer(state[key]),
    }), {})
);

const moduleFilters = Object.values(modulePersistenceReducers)
  .map((m) => m.persistence && m.persistence.filter)
  .map((filter) => filter ? filter : () => true); // eslint-disable-line

const persistenceFilter = (mutation) => moduleFilters.some((filter) => filter(mutation));

const storageKey = () => {
  const { settings } = globals.I;
  const userId = settings.user && settings.user.user_id;
  const systemId = settings.system.system_id;
  return `imperia:${userId}:${systemId}`;
};

const vuexLocal = new VuexPersistence({
  key: storageKey(),
  storage: window.localStorage,
  reducer: persistenceReducer,
  filter: persistenceFilter,
});

export function createStore(router) {
  return _createStore({
    state: {
      ...acl.state,
      ...user.state,
      ...browserMode.state,
      ...locale.state,
      LANG,
    },
    mutations: {
      ...browserMode.mutations,
    },
    actions: {},
    getters: {
      ...acl.getters,
      route() {
        return unref(router?.currentRoute);
      },
    },
    modules,
    plugins: [
      vuexLocal.plugin,
      copyBroadcaster,
    ],
  });
}

export const activeMode = (vm) => vm.$store.state.browserMode;

// Vuex Helpers to dynamically resolve state/getters/mutations/actions
// from the namespaced module associated with the current browser mode
export const mapBrowserState = (mappers) => mapInstanceState(activeMode, mappers);
export const mapBrowserGetters = (mappers) => mapInstanceGetters(activeMode, mappers);
export const mapBrowserMutations = (muts) => mapInstanceMutations(activeMode, muts);
export const mapBrowserActions = (actions) => mapInstanceActions(activeMode, actions);
